/**
 * Local persistence layer (Dexie / IndexedDB).
 *
 * Version 2 introduces the authentication tables (users, devices, sessions,
 * pins). Version 3 adds the fleet tables (vehicles, soldiers). Version 4 adds
 * the task tables (tasks, taskTypes, contacts). Version 5 adds the assignment
 * tables (taskTeams, taskCrew) — the vehicle-bound crews. Version 6 adds the
 * append-only audit ledger (auditLogs), where an assignment made over a
 * warning ("שבץ בכל זאת") is recorded with its reason and its manager.
 * Version 7 adds the field-execution table (taskProgress) — one row per
 * soldier per task: receipt, updates from the field and execution finished.
 * Version 8 adds the operational timeline (timelineEvents) — the append-only
 * progress feed a task is read off, one row per reported step.
 * Version 9 streamlines the soldier record (the three-digit internal number is
 * a vehicle's, never a soldier's) and adds the recycle bin: a task carries a
 * `deletedAt` stamp instead of being removed.
 * Version 10 adds `meta` — the installation's own bookkeeping, one row per
 * fact about the database itself. It is what lets a forced migration run on a
 * device that was seeded by an older build, exactly once.
 * Version 11 opens the vehicle pool to the company's own twenty-eight vehicles:
 * `assignedCompany` is indexed (who is holding the vehicle) and `militaryId`
 * gives up its unique constraint, because the handover sheet carries plates and
 * no internal numbers. Uniqueness over the numbers that *are* recorded is
 * enforced in `src/db/fleet-store.ts` instead.
 * Version 12 adds the quick mission log (quickDriverTasks) — the journeys a
 * manager records for a driver in two taps, which never reached the board and
 * were therefore invisible to the workload graph.
 * Version 13 adds אנשי חוץ (externalPeople) — the directory of people who are
 * not on the unit's מצבה and of the temporary vehicles they arrive in. It is a
 * table of its own on purpose: a contractor must never be counted as a soldier
 * and their van must never appear in the fleet.
 */
import Dexie from 'dexie';
import type { Table } from 'dexie';
import { APP_CONFIG } from '../core/config';
import type { AuthSession, Device, LocalPinRecord, User } from '../types/user';
import type { Soldier, Vehicle } from '../types/fleet';
import type { Task, TaskType } from '../types/task';
import type { Contact } from '../types/contact';
import type { StoredTaskTeam, TaskCrewMember } from '../types/assignment';
import type { AuditLogEntry } from '../types/audit';
import type { SoldierTaskProgress } from '../types/execution';
import type { TimelineEvent } from '../types/timeline';
import type { MetaRecord } from '../types/meta';
import type { QuickDriverTask } from '../types/quick-task';
import type { ExternalPersonRecord } from '../types/external-person';

/** Dedicated database instance for this project alone. */
export const DB_NAME = 'shinua99-tasks-db';
export const DB_VERSION = 13;

export interface Shinua99Database extends Dexie {
  users: Table<User, string>;
  devices: Table<Device, string>;
  sessions: Table<AuthSession, string>;
  pins: Table<LocalPinRecord, string>;
  vehicles: Table<Vehicle, string>;
  soldiers: Table<Soldier, string>;
  tasks: Table<Task, string>;
  taskTypes: Table<TaskType, string>;
  contacts: Table<Contact, string>;
  taskTeams: Table<StoredTaskTeam, string>;
  taskCrew: Table<TaskCrewMember, string>;
  auditLogs: Table<AuditLogEntry, string>;
  taskProgress: Table<SoldierTaskProgress, string>;
  timelineEvents: Table<TimelineEvent, string>;
  meta: Table<MetaRecord, string>;
  quickDriverTasks: Table<QuickDriverTask, string>;
  externalPeople: Table<ExternalPersonRecord, string>;
}

export const db = new Dexie(DB_NAME) as Shinua99Database;

// Version 1 shipped with no stores at all (scaffolding only).
db.version(1).stores({});

// Booleans are not valid IndexedDB keys — `isApproved` is filtered in memory.
db.version(2).stores({
  users: 'id, &phone, role, status',
  devices: 'id, userId, deviceFingerprint, [userId+deviceFingerprint]',
  sessions: 'id, userId',
  pins: 'userId',
});

// Fleet tables. Arrays (`disallowedSoldierIds`, `disallowedVehicleIds`) stay
// unindexed — the matcher reads whole records anyway.
db.version(3).stores({
  vehicles: 'id, &militaryId, plateNumber, vehicleType, weightCategory, status, company',
  soldiers: 'id, &personalNumber, militaryId, userId, company, platoon, availability',
});

// Task tables. `isDefault` / `isSystem` stay unindexed — booleans are not
// valid IndexedDB keys — and the type catalogue is small enough to filter in
// memory. The type name is unique, so a catalogue can never hold two "תדלוק".
db.version(4).stores({
  tasks: 'id, taskTypeId, date, priority, statusId, contactId, createdBy, [date+statusId]',
  taskTypes: 'id, &name',
  contacts: 'id, fullName, primaryPhone, organization',
});

// Assignment tables. A crew row is reached through its team (`teamId`) and a
// soldier's own schedule through `soldierId` — an external passenger carries
// none, so it simply never appears in that index. `passengers` is not stored
// on the team row at all: `taskCrew` is the only place a person lives.
db.version(5).stores({
  taskTeams: 'id, taskId, vehicleId, driverId, escortId, escortOption, [taskId+vehicleId]',
  taskCrew: 'id, teamId, soldierId, crewRole, [teamId+crewRole]',
});

// The audit ledger. Rows are only ever appended, so the table is indexed the
// way it is read back: by the record acted on, by the manager who acted, and
// chronologically. The override payload itself stays unindexed.
db.version(6).stores({
  auditLogs: 'id, action, entityType, entityId, actorId, taskId, createdAt, [entityType+entityId]',
});

// Field execution. A row is reached the two ways it is ever asked for: every
// report on one task, and one soldier's own report on it. `updates` is a plain
// array on the row — a report is only ever read with the row that carries it.
db.version(7).stores({
  taskProgress: 'id, taskId, soldierId, userId, [taskId+soldierId]',
});

// The operational timeline. Rows are only ever appended, so the table is
// indexed the way the feed is read back: the whole task in time order
// (`[taskId+timestamp]`), one vehicle team's own steps (`teamId`), and a step
// type or an actor when a report asks for one. `metadata` stays unindexed —
// it is only ever read with the row that carries it.
db.version(8).stores({
  timelineEvents: 'id, taskId, teamId, eventType, actorId, timestamp, [taskId+timestamp]',
});

// Streamlined soldiers and the recycle bin. The soldier index drops
// `militaryId` — the three-digit internal number is painted on a vehicle, not
// carried by a person — and the task index gains `deletedAt`, so the bin and
// the retention sweep read one indexed column instead of the whole board.
db.version(9)
  .stores({
    soldiers: 'id, &personalNumber, userId, company, platoon, availability',
    tasks: 'id, taskTypeId, date, priority, statusId, contactId, createdBy, deletedAt, [date+statusId]',
  })
  .upgrade(async (tx) => {
    // A record written by an older build still carries the field; drop it so
    // the stored shape matches the type again.
    await tx
      .table('soldiers')
      .toCollection()
      .modify((soldier: Record<string, unknown>) => {
        delete soldier.militaryId;
      });
  });

// The installation's own bookkeeping. One row per fact, keyed by name and read
// through `src/db/meta-store.ts` — `value` is whatever the writer put there, so
// it stays unindexed. A device already carrying data reaches this version with
// an empty `meta` table, which is precisely the signal
// `reconcileSoldierRoster()` reads: no revision on file means the roster
// migration has never run here.
db.version(10).stores({
  meta: 'key',
});

// The vehicle pool the company actually holds. `militaryId` keeps its index but
// loses the `&`: the twenty-eight vehicles came over with plates and no
// internal numbers, and a unique index cannot hold twenty-eight blanks. The
// number is still unique where it is recorded — `createVehicle()` and
// `updateVehicle()` check it against this same index before they write.
// `assignedCompany` joins the index because the roster filters read it.
db.version(11)
  .stores({
    vehicles:
      'id, militaryId, plateNumber, vehicleType, weightCategory, status, company, assignedCompany',
  })
  .upgrade(async (tx) => {
    // A vehicle filed before loans existed never left the company.
    await tx
      .table('vehicles')
      .toCollection()
      .modify((vehicle: Record<string, unknown>) => {
        if (!String(vehicle.assignedCompany ?? '').trim()) vehicle.assignedCompany = 'שינוע';
        if (typeof vehicle.militaryId !== 'string') vehicle.militaryId = '';
      });
  });

// The quick mission log. Rows are only ever appended, so the table is indexed
// the way it is read back: one driver's own missions (`driverId`), the operator
// who filed them (`createdBy`), and chronologically for the audit strip. The
// description stays unindexed — it is only ever read with the row that carries
// it. No upgrade step: a device arriving here simply gains an empty table, which
// is exactly what an installation that has never logged a quick mission has.
db.version(12).stores({
  quickDriverTasks: 'id, driverId, createdBy, createdAt, [driverId+createdAt]',
});

// אנשי חוץ. Its own table, and deliberately not a corner of `soldiers`: the
// מצבה, the availability counters and the licence engine all read that table,
// and a contractor is none of those things. The vehicle an external person
// arrives in is a pair of fields on their card rather than a `vehicles` row, so
// a one-day loan never takes a מספר פנימי or an availability light.
//
// Indexed the three ways the directory is asked for: by name for the picker,
// and by the two numbers `externalPersonKey()` recognises a returning person
// by. No upgrade step — a device arriving here simply gains an empty table.
db.version(DB_VERSION).stores({
  externalPeople: 'id, fullName, phone, personalNumber, lastUsedAt',
});

export const dbInfo = {
  name: DB_NAME,
  version: DB_VERSION,
  appId: APP_CONFIG.appId,
} as const;
